import type { Legend } from '@/components/mapbox/legacy-types'
import MapLayersIconSolid from '@/public/icons/map-layers-icon-solid.svg'
import MapLegendIcon from '@/public/icons/map-legend-icon-outline.svg'
import type { FilterConflict } from '@accounts/types'
import type { IControl, Map } from 'mapbox-gl'

type LoadingOverlayOptions = {
	words?: string[]
	wordInterval?: number
	debugKeepVisible?: boolean
}

const DEFAULT_LOADING_WORDS = [
	'Boring',
	'Trenching',
	'Tunneling',
	'Surveying',
	'Splicing',
	'Gig-Speeding',
	'Fibering',
	'Beaming',
	'Connecting',
	'Digging',
	'Laying Fiber',
	'Light-Speeding',
	'Routing',
	'Networking',
	'Bundling',
	'Transmitting',
	'Pulling Cable',
	'🤖 Terminating'
]

const LOADING_STYLE_ID = 'mx-mapbox-loading-control-styles'
const LOADING_VISIBLE_CLASS = 'mx-mapbox-loading-overlay--visible'
const LOADING_HIDDEN_CLASS = 'mx-mapbox-loading-overlay--hidden'

const ensureLoadingOverlayStyles = () => {
	if (typeof document === 'undefined') {
		return
	}
	if (document.getElementById(LOADING_STYLE_ID)) {
		return
	}
	const styleTag = document.createElement('style')
	styleTag.id = LOADING_STYLE_ID
	styleTag.textContent = `
.mx-mapbox-loading-overlay {
	position: absolute;
	inset: 0;
	display: none;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.6);
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
	z-index: 1100;
	opacity: 0;
	transition: opacity 0.6s ease;
	pointer-events: auto;
}

.mx-mapbox-loading-overlay.${LOADING_VISIBLE_CLASS} {
	display: flex;
	opacity: 1;
	pointer-events: auto;
}

.mx-mapbox-loading-overlay.${LOADING_HIDDEN_CLASS} {
	display: flex;
	opacity: 0;
	pointer-events: none;
}

.mx-mapbox-loading-overlay .mx-loader-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2rem;
	position: relative;
	z-index: 2;
}

.mx-mapbox-loading-overlay .mx-fiber-cables {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 1;
	pointer-events: none;
}

.mx-mapbox-loading-overlay .mx-fiber-path {
	fill: none;
	stroke: #ff5733;
	stroke-width: 2;
	stroke-linecap: round;
	opacity: 0;
	filter: drop-shadow(0 0 4px rgba(255, 87, 51, 0.4));
}

.mx-mapbox-loading-overlay .mx-fiber-branch {
	stroke-width: 1.5;
}

.mx-mapbox-loading-overlay .mx-fiber-path-1 {
	stroke-dasharray: 2200;
	stroke-dashoffset: 2200;
	animation: mx-drawFiber1 12s ease-in-out infinite;
}

.mx-mapbox-loading-overlay .mx-fiber-branch-1a {
	stroke-dasharray: 600;
	stroke-dashoffset: 600;
	animation: mx-drawBranch1a 12s ease-in-out infinite;
}

.mx-mapbox-loading-overlay .mx-fiber-branch-1b {
	stroke-dasharray: 600;
	stroke-dashoffset: 600;
	animation: mx-drawBranch1b 12s ease-in-out infinite;
}

.mx-mapbox-loading-overlay .mx-fiber-path-2 {
	stroke-dasharray: 2400;
	stroke-dashoffset: 2400;
	animation: mx-drawFiber2 15s ease-in-out infinite;
	animation-delay: 3s;
}

.mx-mapbox-loading-overlay .mx-fiber-branch-2a {
	stroke-dasharray: 700;
	stroke-dashoffset: 700;
	animation: mx-drawBranch2a 15s ease-in-out infinite;
	animation-delay: 3s;
}

.mx-mapbox-loading-overlay .mx-fiber-branch-2b {
	stroke-dasharray: 700;
	stroke-dashoffset: 700;
	animation: mx-drawBranch2b 15s ease-in-out infinite;
	animation-delay: 3s;
}

.mx-mapbox-loading-overlay .mx-fiber-path-3 {
	stroke-dasharray: 2200;
	stroke-dashoffset: 2200;
	animation: mx-drawFiber3 18s ease-in-out infinite;
	animation-delay: 6s;
}

.mx-mapbox-loading-overlay .mx-fiber-branch-3a {
	stroke-dasharray: 650;
	stroke-dashoffset: 650;
	animation: mx-drawBranch3a 18s ease-in-out infinite;
	animation-delay: 6s;
}

.mx-mapbox-loading-overlay .mx-fiber-branch-3b {
	stroke-dasharray: 650;
	stroke-dashoffset: 650;
	animation: mx-drawBranch3b 18s ease-in-out infinite;
	animation-delay: 6s;
}

@keyframes mx-drawFiber1 {
	0% {
		stroke-dashoffset: 2200;
		opacity: 0;
	}
	5% {
		opacity: 0.3;
	}
	50% {
		stroke-dashoffset: 0;
		opacity: 0.3;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.3;
	}
}

@keyframes mx-drawBranch1a {
	0% {
		stroke-dashoffset: 600;
		opacity: 0;
	}
	50% {
		stroke-dashoffset: 600;
		opacity: 0;
	}
	52% {
		opacity: 0.25;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.25;
	}
}

@keyframes mx-drawBranch1b {
	0% {
		stroke-dashoffset: 600;
		opacity: 0;
	}
	50% {
		stroke-dashoffset: 600;
		opacity: 0;
	}
	53% {
		opacity: 0.25;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.25;
	}
}

@keyframes mx-drawFiber2 {
	0% {
		stroke-dashoffset: 2400;
		opacity: 0;
	}
	5% {
		opacity: 0.25;
	}
	50% {
		stroke-dashoffset: 0;
		opacity: 0.25;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.25;
	}
}

@keyframes mx-drawBranch2a {
	0% {
		stroke-dashoffset: 700;
		opacity: 0;
	}
	50% {
		stroke-dashoffset: 700;
		opacity: 0;
	}
	52% {
		opacity: 0.2;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.2;
	}
}

@keyframes mx-drawBranch2b {
	0% {
		stroke-dashoffset: 700;
		opacity: 0;
	}
	50% {
		stroke-dashoffset: 700;
		opacity: 0;
	}
	53% {
		opacity: 0.2;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.2;
	}
}

@keyframes mx-drawFiber3 {
	0% {
		stroke-dashoffset: 2200;
		opacity: 0;
	}
	5% {
		opacity: 0.2;
	}
	50% {
		stroke-dashoffset: 0;
		opacity: 0.2;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.2;
	}
}

@keyframes mx-drawBranch3a {
	0% {
		stroke-dashoffset: 650;
		opacity: 0;
	}
	50% {
		stroke-dashoffset: 650;
		opacity: 0;
	}
	52% {
		opacity: 0.18;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.18;
	}
}

@keyframes mx-drawBranch3b {
	0% {
		stroke-dashoffset: 650;
		opacity: 0;
	}
	50% {
		stroke-dashoffset: 650;
		opacity: 0;
	}
	53% {
		opacity: 0.18;
	}
	100% {
		stroke-dashoffset: 0;
		opacity: 0.18;
	}
}

.mx-mapbox-loading-overlay .mx-logo-wrapper {
	position: relative;
	width: 160px;
	height: 130px;
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 2;
}

.mx-mapbox-loading-overlay .mx-glow-circle {
	position: absolute;
	width: 180px;
	height: 180px;
	border-radius: 50%;
	background: radial-gradient(circle, rgba(240, 249, 255, 0.9) 0%, rgba(240, 249, 255, 0.6) 30%, rgba(240, 249, 255, 0.3) 50%, transparent 70%);
	animation: mx-pulse 2s ease-in-out infinite;
	filter: blur(30px);
}

.mx-mapbox-loading-overlay .mx-logo {
	position: relative;
	z-index: 1;
	width: 130px;
	height: 105px;
	filter: drop-shadow(0 0 20px rgba(255, 87, 51, 0.8));
}

.mx-mapbox-loading-overlay .mx-logo-path {
	fill: none;
	stroke: white;
	stroke-width: 2;
	stroke-linecap: round;
	stroke-linejoin: round;
	stroke-dasharray: 1000;
	stroke-dashoffset: 1000;
	animation: mx-drawPath 3s ease-in-out infinite;
}

.mx-mapbox-loading-overlay .mx-logo-path-fill {
	fill: white;
	opacity: 0;
	animation: mx-fadeInFill 3s ease-in-out infinite;
}

@keyframes mx-drawPath {
	0% {
		stroke-dashoffset: 1000;
		opacity: 1;
	}
	50% {
		stroke-dashoffset: 0;
		opacity: 1;
	}
	100% {
		stroke-dashoffset: 1000;
		opacity: 1;
	}
}

@keyframes mx-fadeInFill {
	0% {
		opacity: 0;
	}
	40% {
		opacity: 0;
	}
	50% {
		opacity: 1;
	}
	90% {
		opacity: 1;
	}
	100% {
		opacity: 0;
	}
}

@keyframes mx-pulse {
	0%, 100% {
		transform: scale(0.85);
		opacity: 0.95;
	}
	50% {
		transform: scale(1.15);
		opacity: 0.7;
	}
}

.mx-mapbox-loading-overlay .mx-loading-text {
	color: white;
	font-size: 1.2rem;
	font-weight: 300;
	letter-spacing: 0.2em;
	text-transform: uppercase;
	opacity: 0.9;
	animation: mx-textPulse 2s ease-in-out infinite;
	position: relative;
	left: 0.6em;
}

@keyframes mx-textPulse {
	0%, 100% {
		opacity: 0.6;
	}
	50% {
		opacity: 1;
	}
}

.mx-mapbox-loading-overlay .mx-dots {
	display: inline-block;
}

.mx-mapbox-loading-overlay .mx-dot {
	animation: mx-dotPulse 1.5s ease-in-out infinite;
	opacity: 0;
}

.mx-mapbox-loading-overlay .mx-dot:nth-child(1) {
	animation-delay: 0s;
}

.mx-mapbox-loading-overlay .mx-dot:nth-child(2) {
	animation-delay: 0.3s;
}

.mx-mapbox-loading-overlay .mx-dot:nth-child(3) {
	animation-delay: 0.6s;
}

@keyframes mx-dotPulse {
	0%, 100% {
		opacity: 0;
	}
	50% {
		opacity: 1;
	}
}
	`
	document.head.appendChild(styleTag)
}

class LoadingOverlayControl implements IControl {
	private options: LoadingOverlayOptions
	private map?: Map
	private placeholder: HTMLDivElement | null
	private overlay: HTMLDivElement | null
	private wordElement: HTMLSpanElement | null
	private intervalId: number | null
	private currentIndex: number
	private isVisible: boolean
	private keepVisibleOnLoad: boolean
	private readonly transitionDuration = 600

	constructor(options: LoadingOverlayOptions = {}) {
		this.options = {
			...options,
			words: options.words ?? DEFAULT_LOADING_WORDS,
			wordInterval: options.wordInterval ?? 6000,
			debugKeepVisible: options.debugKeepVisible ?? false
		}
		this.placeholder = null
		this.overlay = null
		this.wordElement = null
		this.intervalId = null
		this.currentIndex = Math.floor(Math.random() * this.options.words!.length)
		this.isVisible = false
		this.keepVisibleOnLoad = this.options.debugKeepVisible ?? false
	}

	private createTemplate() {
		return `
    <svg class="mx-fiber-cables" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <g>
        <path class="mx-fiber-path mx-fiber-path-1" d="M-100,300 Q200,250 400,400 T800,350 Q1000,300 1200,450 T1600,400 Q1800,350 2100,500" />
        <path class="mx-fiber-path mx-fiber-branch mx-fiber-branch-1a" d="M1600,400 Q1700,350 1800,470 T2000,430 Q2050,400 2100,520" />
        <path class="mx-fiber-path mx-fiber-branch mx-fiber-branch-1b" d="M1600,400 Q1750,500 1850,430 T2000,530 Q2050,480 2100,600" />
      </g>
      <g>
        <path class="mx-fiber-path mx-fiber-path-2" d="M-100,600 Q300,700 600,550 T1000,650 Q1300,600 1500,750 T1900,700 Q2000,650 2100,800" />
        <path class="mx-fiber-path mx-fiber-branch mx-fiber-branch-2a" d="M1900,700 Q1950,650 2000,770 T2050,730 Q2080,700 2100,820" />
        <path class="mx-fiber-path mx-fiber-branch mx-fiber-branch-2b" d="M1900,700 Q2000,800 2050,730 T2080,830 Q2090,780 2100,900" />
      </g>
      <g>
        <path class="mx-fiber-path mx-fiber-path-3" d="M2100,200 Q1800,150 1500,300 T1100,250 Q900,200 700,350 T300,300 Q100,250 -100,400" />
        <path class="mx-fiber-path mx-fiber-branch mx-fiber-branch-3a" d="M300,300 Q150,250 50,370 T-50,330 Q-75,300 -100,420" />
        <path class="mx-fiber-path mx-fiber-branch mx-fiber-branch-3b" d="M300,300 Q200,400 50,330 T-50,430 Q-75,380 -100,520" />
      </g>
    </svg>
    <div class="mx-loader-container">
      <div class="mx-logo-wrapper">
        <div class="mx-glow-circle"></div>
        <svg class="mx-logo" viewBox="-2 -2 56 46" xmlns="http://www.w3.org/2000/svg">
          <path class="mx-logo-path" d="M39.1935 39.6191L40.926 41.7191C41.0568 41.8832 41.2856 41.916 41.449 41.7847C48.0194 36.8628 51.8767 29.1519 51.8767 20.916C51.8767 12.7456 48.0848 5.10034 41.6125 0.17846C41.3183 -0.0512273 41.0241 0.0143977 40.8933 0.211273L39.1935 2.24565C38.9647 2.50815 39.0955 2.83627 39.2589 2.96752C44.914 7.20034 48.2483 13.8285 48.2483 20.9488C48.2483 28.0363 44.914 34.6644 39.2589 38.8972C38.9974 39.0613 38.9974 39.3894 39.1935 39.6191Z"/>
          <path class="mx-logo-path" d="M34.4209 33.9097L36.186 36.0097C36.3168 36.1738 36.5456 36.2066 36.7091 36.0754C41.6123 32.5644 44.5216 26.9535 44.5216 20.8832C44.5216 14.8785 41.6777 9.30035 36.8398 5.78941C36.5783 5.62535 36.3168 5.65816 36.1533 5.82222L34.4535 7.8566C34.192 8.18472 34.3882 8.51285 34.5843 8.6441C38.5396 11.466 40.8932 15.9941 40.8932 20.8832C40.8932 25.7722 38.5396 30.3332 34.5516 33.155C34.2574 33.3519 34.2901 33.7457 34.4209 33.9097Z"/>
          <path class="mx-logo-path" d="M25.1704 22.8191L20.3324 17.0113C20.0709 16.6832 19.646 16.6503 19.4172 17.0769C18.7307 18.2253 18.4038 19.5378 18.4038 20.8832C18.4038 23.541 19.8094 26.0019 22.0649 27.38C22.3918 27.5769 22.3918 28.0035 22.1957 28.2003L20.4632 30.2675C20.3324 30.4316 20.1363 30.4644 19.9729 30.3332C16.7367 28.266 14.7754 24.6894 14.7754 20.8503C14.7754 17.405 16.3444 14.1894 18.9922 12.0566C19.7441 11.4332 20.5286 11.4988 20.9862 12.0566L25.7914 17.7988C25.8241 17.8644 25.8895 17.8972 25.9549 17.8972C26.0203 17.8972 26.0856 17.8644 26.1183 17.7988L30.9236 12.0566C31.3812 11.4988 32.133 11.4332 32.9176 12.0566C35.5653 14.1894 37.1344 17.405 37.1344 20.8503C37.1344 24.6894 35.1731 28.266 31.9369 30.3332C31.7735 30.4316 31.5773 30.3988 31.4466 30.2675L29.6814 28.2332C29.4853 28.0363 29.4853 27.5769 29.8121 27.4128C32.0677 26.0347 33.4733 23.5738 33.4733 20.916C33.4733 19.5707 33.1137 18.2582 32.4599 17.1097C32.1984 16.6832 31.7735 16.716 31.5446 17.0441L26.7067 22.8191C26.5106 23.0488 26.2164 23.18 25.9549 23.18C25.6607 23.18 25.3665 23.0488 25.1704 22.8191Z"/>
          <path class="mx-logo-path" d="M12.6832 39.6191L10.9507 41.7191C10.8199 41.8832 10.5911 41.916 10.4277 41.7847C3.85725 36.83 0 29.1191 0 20.8832C0 12.7128 3.79188 5.10034 10.2642 0.145654C10.5584 -0.0840337 10.8526 -0.0184087 10.9834 0.178466L12.6832 2.21284C12.912 2.47534 12.7812 2.80347 12.6178 2.93472C6.96267 7.16753 3.62843 13.7957 3.62843 20.8832C3.62843 27.9707 6.96267 34.5988 12.6178 38.8316C12.8793 39.0613 12.8793 39.3894 12.6832 39.6191Z"/>
          <path class="mx-logo-path" d="M17.4231 33.9097L15.6579 36.0097C15.5271 36.1738 15.2983 36.2066 15.1349 36.0754C10.2316 32.5644 7.35498 26.9535 7.35498 20.8832C7.35498 14.8785 10.1989 9.30035 15.0368 5.78941C15.2983 5.62535 15.5598 5.65816 15.7233 5.82222L17.4231 7.8566C17.6846 8.18472 17.4884 8.51285 17.2923 8.6441C13.337 11.466 10.9834 15.9941 10.9834 20.8832C10.9834 25.7722 13.337 30.3332 17.325 33.155C17.6192 33.3519 17.5865 33.7457 17.4231 33.9097Z"/>
          <path class="mx-logo-path-fill" d="M39.1935 39.6191L40.926 41.7191C41.0568 41.8832 41.2856 41.916 41.449 41.7847C48.0194 36.8628 51.8767 29.1519 51.8767 20.916C51.8767 12.7456 48.0848 5.10034 41.6125 0.17846C41.3183 -0.0512273 41.0241 0.0143977 40.8933 0.211273L39.1935 2.24565C38.9647 2.50815 39.0955 2.83627 39.2589 2.96752C44.914 7.20034 48.2483 13.8285 48.2483 20.9488C48.2483 28.0363 44.914 34.6644 39.2589 38.8972C38.9974 39.0613 38.9974 39.3894 39.1935 39.6191Z"/>
          <path class="mx-logo-path-fill" d="M34.4209 33.9097L36.186 36.0097C36.3168 36.1738 36.5456 36.2066 36.7091 36.0754C41.6123 32.5644 44.5216 26.9535 44.5216 20.8832C44.5216 14.8785 41.6777 9.30035 36.8398 5.78941C36.5783 5.62535 36.3168 5.65816 36.1533 5.82222L34.4535 7.8566C34.192 8.18472 34.3882 8.51285 34.5843 8.6441C38.5396 11.466 40.8932 15.9941 40.8932 20.8832C40.8932 25.7722 38.5396 30.3332 34.5516 33.155C34.2574 33.3519 34.2901 33.7457 34.4209 33.9097Z"/>
          <path class="mx-logo-path-fill" d="M25.1704 22.8191L20.3324 17.0113C20.0709 16.6832 19.646 16.6503 19.4172 17.0769C18.7307 18.2253 18.4038 19.5378 18.4038 20.8832C18.4038 23.541 19.8094 26.0019 22.0649 27.38C22.3918 27.5769 22.3918 28.0035 22.1957 28.2003L20.4632 30.2675C20.3324 30.4316 20.1363 30.4644 19.9729 30.3332C16.7367 28.266 14.7754 24.6894 14.7754 20.8503C14.7754 17.405 16.3444 14.1894 18.9922 12.0566C19.7441 11.4332 20.5286 11.4988 20.9862 12.0566L25.7914 17.7988C25.8241 17.8644 25.8895 17.8972 25.9549 17.8972C26.0203 17.8972 26.0856 17.8644 26.1183 17.7988L30.9236 12.0566C31.3812 11.4988 32.133 11.4332 32.9176 12.0566C35.5653 14.1894 37.1344 17.405 37.1344 20.8503C37.1344 24.6894 35.1731 28.266 31.9369 30.3332C31.7735 30.4316 31.5773 30.3988 31.4466 30.2675L29.6814 28.2332C29.4853 28.0363 29.4853 27.5769 29.8121 27.4128C32.0677 26.0347 33.4733 23.5738 33.4733 20.916C33.4733 19.5707 33.1137 18.2582 32.4599 17.1097C32.1984 16.6832 31.7735 16.716 31.5446 17.0441L26.7067 22.8191C26.5106 23.0488 26.2164 23.18 25.9549 23.18C25.6607 23.18 25.3665 23.0488 25.1704 22.8191Z"/>
          <path class="mx-logo-path-fill" d="M12.6832 39.6191L10.9507 41.7191C10.8199 41.8832 10.5911 41.916 10.4277 41.7847C3.85725 36.83 0 29.1191 0 20.8832C0 12.7128 3.79188 5.10034 10.2642 0.145654C10.5584 -0.0840337 10.8526 -0.0184087 10.9834 0.178466L12.6832 2.21284C12.912 2.47534 12.7812 2.80347 12.6178 2.93472C6.96267 7.16753 3.62843 13.7957 3.62843 20.8832C3.62843 27.9707 6.96267 34.5988 12.6178 38.8316C12.8793 39.0613 12.8793 39.3894 12.6832 39.6191Z"/>
          <path class="mx-logo-path-fill" d="M17.4231 33.9097L15.6579 36.0097C15.5271 36.1738 15.2983 36.2066 15.1349 36.0754C10.2316 32.5644 7.35498 26.9535 7.35498 20.8832C7.35498 14.8785 10.1989 9.30035 15.0368 5.78941C15.2983 5.62535 15.5598 5.65816 15.7233 5.82222L17.4231 7.8566C17.6846 8.18472 17.4884 8.51285 17.2923 8.6441C13.337 11.466 10.9834 15.9941 10.9834 20.8832C10.9834 25.7722 13.337 30.3332 17.325 33.155C17.6192 33.3519 17.5865 33.7457 17.4231 33.9097Z"/>
        </svg>
      </div>
      <div class="mx-loading-text">
        <span data-loading-word>Loading</span><span class="mx-dots"><span class="mx-dot">.</span><span class="mx-dot">.</span><span class="mx-dot">.</span></span>
      </div>
    </div>
    `
	}

	onAdd(map: Map) {
		this.map = map
		const doc = map.getCanvas().ownerDocument
		ensureLoadingOverlayStyles()
		this.placeholder = doc.createElement('div')
		this.placeholder.className = 'mapboxgl-ctrl mx-mapbox-loading-overlay__placeholder'
		this.placeholder.style.display = 'none'
		this.overlay = doc.createElement('div')
		this.overlay.className = 'mx-mapbox-loading-overlay'
		this.overlay.innerHTML = this.createTemplate()
		this.wordElement = this.overlay.querySelector('[data-loading-word]') as HTMLSpanElement | null
		const container = map.getContainer()
		container.appendChild(this.overlay)
		this.startWordRotation()
		this.show()
		return this.placeholder
	}

	onRemove() {
		if (this.intervalId) {
			window.clearInterval(this.intervalId)
			this.intervalId = null
		}
		this.map = undefined
		if (this.overlay?.parentNode) {
			this.overlay.parentNode.removeChild(this.overlay)
		}
		this.overlay = null
		this.wordElement = null
	}

	show() {
		if (this.isVisible) {
			return
		}
		this.isVisible = true
		if (!this.overlay) {
			return
		}
		this.overlay.style.display = 'flex'
		this.overlay.classList.remove(LOADING_HIDDEN_CLASS)
		this.overlay.classList.add(LOADING_VISIBLE_CLASS)
	}

	hide(force = false) {
		if (this.keepVisibleOnLoad && !force) {
			this.show()
			return
		}
		if (!this.isVisible && !force) {
			return
		}
		this.isVisible = false
		if (!this.overlay) {
			return
		}
		this.overlay.classList.remove(LOADING_VISIBLE_CLASS)
		this.overlay.classList.add(LOADING_HIDDEN_CLASS)
		window.setTimeout(() => {
			if (!this.isVisible && this.overlay) {
				this.overlay.classList.remove(LOADING_HIDDEN_CLASS)
				this.overlay.style.display = 'none'
			}
		}, this.transitionDuration)
	}

	setWords(words: string[]) {
		if (!words?.length) {
			return
		}
		this.options.words = words
		this.currentIndex = Math.floor(Math.random() * words.length)
		this.updateWord()
	}

	setDebugKeepVisible(keepVisible: boolean) {
		this.keepVisibleOnLoad = keepVisible
		this.options.debugKeepVisible = keepVisible
		if (keepVisible) {
			this.show()
			return
		}
		this.hide(true)
	}

	isDebugKeepVisible() {
		return this.keepVisibleOnLoad
	}

	private startWordRotation() {
		if (!this.options.words?.length || typeof window === 'undefined') {
			return
		}
		this.updateWord()
		if (this.intervalId) {
			window.clearInterval(this.intervalId)
		}
		this.intervalId = window.setInterval(() => {
			this.updateWord()
		}, this.options.wordInterval)
	}

	private updateWord() {
		if (!this.wordElement || !this.options.words?.length) {
			return
		}
		this.wordElement.textContent = this.options.words[this.currentIndex]
		this.currentIndex = (this.currentIndex + 1) % this.options.words.length
	}
}

class StyleSwitcherControl implements IControl {
	styles: { label: string
		url: string }[]

	container: HTMLDivElement
	buttonContainer: HTMLDivElement
	contentContainer: HTMLDivElement
	isExpanded: boolean
	currentStyle: string
	map?: Map

	constructor(styles: { label: string
		url: string }[]) {
		this.styles = styles
		this.isExpanded = false

		// Use "Map Style" instead of trying to determine the initial style
		this.currentStyle = 'Map Style'

		// Main container for the entire control
		this.container = document.createElement('div')
		this.container.className = 'mapboxgl-ctrl font-bold pointer-events-auto max-w-[10rem]'

		// Button container for the toggle button
		this.buttonContainer = document.createElement('div')
		this.buttonContainer.className = 'flex items-center justify-between px-2 py-1 bg-white border-2 rounded shadow-sm cursor-pointer border-black/10'
		this.buttonContainer.onclick = () => this.toggleDropdown()

		// Create the button content with label and dropdown arrow
		const buttonLabel = document.createElement('span')
		buttonLabel.textContent = this.currentStyle
		buttonLabel.className = 'pr-2'

		const dropdownArrow = document.createElement('span')
		dropdownArrow.innerHTML = '▼'
		dropdownArrow.className = 'text-xs transition-transform duration-300 ease-in-out'
		dropdownArrow.style.transform = 'rotate(0deg)'

		this.buttonContainer.appendChild(buttonLabel)
		this.buttonContainer.appendChild(dropdownArrow)
		this.container.appendChild(this.buttonContainer)

		// Content container for the dropdown options
		this.contentContainer = document.createElement('div')
		this.contentContainer.className = 'overflow-hidden transition-all duration-300 ease-in-out bg-white border-2 rounded shadow-sm border-black/10'

		// Set initial collapsed state
		this.contentContainer.style.maxHeight = '0'
		this.contentContainer.style.opacity = '0'
		this.contentContainer.style.marginTop = '4px'
		this.contentContainer.style.transform = 'scaleY(0)'
		this.contentContainer.style.transformOrigin = 'top'

		// Add style options to the dropdown - without highlighting any by default
		this.styles.forEach(style => {
			const option = document.createElement('div')
			option.className = 'flex items-center justify-between w-full px-3 py-2 text-left transition-colors duration-200 hover:bg-mx-gray-100'

			// Add text span for the style label
			const labelSpan = document.createElement('span')
			labelSpan.textContent = style.label
			option.appendChild(labelSpan)

			// Add checkmark for selected style (hidden by default)
			const checkmark = document.createElement('span')
			checkmark.textContent = '✓'
			checkmark.className = 'ml-2 font-bold text-mx-orange'
			checkmark.style.display = 'none'
			option.appendChild(checkmark)

			option.style.cursor = 'pointer'
			option.onclick = e => {
				e.stopPropagation() // Prevent the dropdown toggle from firing
				this.setStyle(style, buttonLabel, dropdownArrow, this.map)

				// Update checkmarks and styles
				this.contentContainer.childNodes.forEach((child: Node) => {
					const element = child as HTMLElement
					const checkEl = element.querySelector('span:last-child') as HTMLElement

					if (element.textContent?.includes(style.label)) {
						element.classList.add('bg-mx-gray-100')
						checkEl.style.display = 'block'
					} else {
						element.classList.remove('bg-mx-gray-100')
						checkEl.style.display = 'none'
					}
				})
			}

			this.contentContainer.appendChild(option)
		})

		this.container.appendChild(this.contentContainer)

		// Close dropdown when clicking outside
		document.addEventListener('click', e => {
			if (this.isExpanded && !this.container.contains(e.target as Node)) {
				this.isExpanded = false
				this.collapseDropdown(dropdownArrow)
			}
		})
	}

	onAdd(map: Map) {
		this.map = map
		return this.container
	}

	toggleDropdown() {
		this.isExpanded = !this.isExpanded
		const dropdownArrow = this.buttonContainer.children[1] as HTMLSpanElement

		if (this.isExpanded) {
			this.contentContainer.style.maxHeight = `${this.contentContainer.scrollHeight}px`
			this.contentContainer.style.opacity = '1'
			this.contentContainer.style.marginTop = '0.25rem' // mt-1
			this.contentContainer.style.marginBottom = '6px'
			this.contentContainer.style.transform = 'scaleY(1)'
			dropdownArrow.style.transform = 'rotate(180deg)'
		} else {
			this.collapseDropdown(dropdownArrow)
		}
	}

	collapseDropdown(arrowElement: HTMLSpanElement) {
		this.contentContainer.style.maxHeight = '0'
		this.contentContainer.style.opacity = '0'
		this.contentContainer.style.marginTop = '4px'
		this.contentContainer.style.marginBottom = '0'
		this.contentContainer.style.transform = 'scaleY(0)'
		arrowElement.style.transform = 'rotate(0deg)'
	}

	setStyle(style: { label: string
		url: string }, labelElement: HTMLSpanElement, arrowElement: HTMLSpanElement, map?: Map) {
		this.currentStyle = style.label
		labelElement.textContent = style.label

		// Close the dropdown
		this.isExpanded = false
		this.collapseDropdown(arrowElement)

		// Set the map style
		if (map) {
			map.setStyle(style.url)
		}
	}

	onRemove() {
		this.container.parentNode?.removeChild(this.container)
	}
}
class LayerVisibilityControl implements IControl {
	layers: { name: string
		id: string
		visibility: boolean
		hideInVisibilityControl: boolean
		siblingLayerIds: string[] }[]

	container: HTMLDivElement
	buttonContainer: HTMLDivElement
	contentContainer: HTMLDivElement
	isExpanded: boolean
	map: Map

	constructor(map: Map, layers: { name: string
		id: string
		visibility: boolean
		hideInVisibilityControl: boolean
		siblingLayerIds: string[] }[]) {
		this.map = map
		this.layers = layers.map(layer => ({
			name: layer.name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ').replace(' Layer', ''),
			id: layer.id,
			visibility: layer.visibility,
			hideInVisibilityControl: layer.hideInVisibilityControl,
			siblingLayerIds: layer.siblingLayerIds
		}))

		this.isExpanded = false

		// Main container for the entire control
		this.container = document.createElement('div')
		this.container.className = 'mapboxgl-ctrl font-bold pointer-events-auto max-w-[12rem]'

		// Button container for the toggle button
		this.buttonContainer = document.createElement('div')
		this.buttonContainer.className = 'flex items-center justify-end ml-auto w-[33px] h-[33px] -mr-0.5 rounded border-2 border-black/10'

		// Button to toggle visibility of the layers
		const toggleButton = document.createElement('button')
		toggleButton.className = 'w-full h-full p-1 rounded shadow-sm cursor-pointer'
		toggleButton.style.backgroundColor = 'white'
		toggleButton.style.transition = 'background-color 0.1s ease'
		toggleButton.onmouseenter = () => {
			toggleButton.style.backgroundColor = '#E6E6E6'
		}
		toggleButton.onmouseleave = () => {
			toggleButton.style.backgroundColor = 'white'
		}
		toggleButton.onclick = () => this.toggleVisibility()

		// Using the imported SVG as an image
		const img = document.createElement('img')
		img.src = MapLayersIconSolid
		img.alt = 'Layer Visibility'
		img.className = 'w-[20px] h-[20px] m-auto rounded text-mx-gray-500'

		toggleButton.appendChild(img)

		this.buttonContainer.appendChild(toggleButton)
		this.container.appendChild(this.buttonContainer)

		// Content container for the layer buttons
		this.contentContainer = document.createElement('div')
		this.contentContainer.className = 'mt-1 overflow-hidden transition-all duration-300 ease-in-out bg-white rounded shadow-sm max-h-0'

		this.layers.forEach(layer => {
			// Skip layers that should not be shown in the visibility control
			if (layer.hideInVisibilityControl) {
				return
			}
			// Create a button for each layer
			const button = document.createElement('button')
			button.className = 'flex items-center justify-between w-full px-2 py-1 my-0 ml-auto shadow-sm cursor-pointer first-of-type:rounded-t last-of-type:rounded-b'
			button.id = layer.id
			button.innerHTML = `<span class="mr-4">${layer.visibility ? '✅' : '❌'}</span>`
			button.innerHTML += `<span>${layer.visibility ? `${layer.name}` : `${layer.name}`}</span>`
			button.style.backgroundColor = 'white'
			button.style.transition = 'background-color 0.1s ease'
			button.onmouseenter = () => {
				button.style.backgroundColor = '#E6E6E6'
			}
			button.onmouseleave = () => {
				button.style.backgroundColor = 'white'
			}
			button.onclick = () => {
				// Ensure the layer is loaded before toggling visibility
				if (!this.map?.isStyleLoaded()) {
					devOnly(() => {
						console.log('Map style is not loaded yet.')
					})
					return
				}
				// Check if the layer exists before trying to set its visibility
				if (!this.map?.getLayer(layer.id)) {
					devOnly(() => {
						console.log(`Layer ${layer.id} does not exist in the map style.`)
					})
					return
				}
				// Toggle the visibility of the layer
				const visibility = this.map?.getLayoutProperty(layer.id, 'visibility')

				button.innerHTML = `<span class="mr-4">${visibility === 'none' ? '✅' : '❌'}</span>`
				button.innerHTML += `<span>${layer.name}</span>`

				this.map?.setLayoutProperty(layer.id, 'visibility', visibility === 'visible' ? 'none' : 'visible')

				if (layer.siblingLayerIds?.length) {
					layer.siblingLayerIds.forEach(siblingLayerId => {
						this.map?.setLayoutProperty(siblingLayerId, 'visibility', visibility === 'visible' ? 'none' : 'visible')
					})
				}
			}
			this.contentContainer.appendChild(button)
		})

		this.container.appendChild(this.contentContainer)
	}

	onAdd() {
		return this.container
	}

	toggleVisibility() {
		this.isExpanded = !this.isExpanded
		this.contentContainer.style.maxHeight = this.isExpanded ? `${this.contentContainer.scrollHeight}px` : '0'
	}

	updateLayers(layers: { name: string
		id: string
		visibility: boolean
		hideInVisibilityControl: boolean
		siblingLayerIds: string[] }[]) {
		this.layers = layers
		this.layers.forEach(layer => {
			if (layer.hideInVisibilityControl) {
				return
			}
			const button = Array.from(this.contentContainer.children)?.find((child: Element) => {
				const button = child as HTMLButtonElement
				return button.id === layer.id
			})
			const layerName = layer.name.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ').replace(' Layer', '')

			if (button) {
				button.innerHTML = `<span class="mr-4">${layer.visibility ? '✅' : '❌'}</span>`
				button.innerHTML += `<span>${layerName}</span>`
			}
		})
	}

	onRemove() {
		this.container.parentNode?.removeChild(this.container)
	}
}

class ActivePolygonControl implements IControl {
	container: HTMLDivElement
	labelContainer: HTMLDivElement
	activePolygonLabel: string
	clearActivePolygon: () => void

	constructor(activePolygonLabel: string, clearActivePolygon: () => void) {
		this.activePolygonLabel = activePolygonLabel
		this.clearActivePolygon = clearActivePolygon
		this.container = document.createElement('div')
		this.container.className = 'pointer-events-auto mapboxgl-ctrl'
		this.labelContainer = document.createElement('div')
		this.labelContainer.className = 'flex items-center justify-between p-2 bg-white rounded shadow-md'

		// Initialize visibility based on the initial label
		this.setVisibility()
	}

	onAdd() {
		const text = document.createElement('p')
		text.textContent = `${this.activePolygonLabel}`
		text.className = 'pr-2 text-sm font-bold capitalize whitespace-nowrap'
		this.labelContainer.appendChild(text)

		const button = document.createElement('button')
		button.textContent = 'Reset'
		button.className = 'px-2 py-1 text-white rounded shadow-sm'
		button.style.backgroundColor = '#FF5733'
		button.style.transition = 'background-color 0.1s ease'
		button.onmouseenter = () => {
			button.style.backgroundColor = 'rgba(255, 87, 47, 0.8)'
		}
		button.onmouseleave = () => {
			button.style.backgroundColor = '#FF5733'
		}
		button.onclick = this.clearActivePolygon
		this.labelContainer.appendChild(button)
		this.container.appendChild(this.labelContainer)

		return this.container
	}

	updateLabel(activePolygonLabel: string) {
		this.activePolygonLabel = activePolygonLabel
		const text = this.labelContainer.children[0] as HTMLParagraphElement
		text.textContent = `${this.activePolygonLabel}`
		this.setVisibility()
	}

	setVisibility() {
		// Hide the container if the label is empty, show it otherwise
		this.container.style.display = this.activePolygonLabel ? 'flex' : 'none'
	}

	onRemove() {
		this.container.parentNode?.removeChild(this.container)
	}
}

class LegendControl implements IControl {
	container: HTMLDivElement
	buttonContainer: HTMLDivElement
	contentContainer: HTMLDivElement
	contentInnerContainer: HTMLDivElement
	legends: Legend[]
	isExpanded: boolean
	map: Map

	constructor(legends: Legend[], map: Map) {
		this.legends = legends
		this.map = map
		this.isExpanded = false

		// Main container for the entire control
		this.container = document.createElement('div')
		this.container.className = 'font-bold pointer-events-auto mapboxgl-ctrl'
		this.container.style.maxWidth = 'calc(100vw - 20px)' // Responsive with 10px margin on each side
		this.container.style.margin = '0 10px' // Explicit margin on both sides

		// Button container for the toggle button
		this.buttonContainer = document.createElement('div')
		this.buttonContainer.className = 'flex items-center justify-start'

		// Button to toggle visibility of the legend content
		const toggleButton = document.createElement('button')
		toggleButton.className = 'p-1 rounded border-2 border-black/10 shadow-sm cursor-pointer w-[48px] h-[34px]'
		toggleButton.style.backgroundColor = 'white'
		toggleButton.style.transition = 'background-color 0.1s ease'
		toggleButton.onmouseenter = () => {
			toggleButton.style.backgroundColor = '#E6E6E6'
		}
		toggleButton.onmouseleave = () => {
			toggleButton.style.backgroundColor = 'white'
		}
		toggleButton.onclick = () => this.toggleVisibility()

		const img = document.createElement('img')
		img.src = MapLegendIcon
		img.alt = 'Toggle Legend'
		img.className = 'w-[40px] h-auto m-auto rounded text-mx-gray-500'

		toggleButton.appendChild(img)
		this.buttonContainer.appendChild(toggleButton)
		this.container.appendChild(this.buttonContainer)

		// Content container for the legend items
		this.contentContainer = document.createElement('div')
		this.contentContainer.className = 'mt-1 overflow-hidden transition-all duration-300 ease-in-out rounded shadow-sm bg-white/95'
		this.contentContainer.style.maxHeight = '0'
		this.contentContainer.style.opacity = '0'
		this.contentContainer.style.transform = 'scaleY(0)'
		this.contentContainer.style.transformOrigin = 'top'
		this.contentContainer.style.transition = 'max-height 0.3s ease, opacity 0.3s ease, transform 0.3s ease'
		this.contentContainer.style.maxWidth = '470px' // Maximum width of 470px for content (450px + 20px padding)
		this.contentContainer.style.zIndex = '100' // High z-index to appear above other controls
		this.contentContainer.style.position = 'relative' // Ensure z-index works properly

		this.contentInnerContainer = document.createElement('div')
		this.contentInnerContainer.className = 'p-3 border-2 rounded border-black/10'
		this.contentInnerContainer.style.maxWidth = '470px' // Match the container max-width
		this.contentInnerContainer.style.boxSizing = 'border-box' // Include padding in width calculation

		this.legends.forEach(legend => {
			const section = document.createElement('div')
			section.className = 'w-full mb-4 last:mb-0'

			const title = document.createElement('div')
			title.className = 'pb-1 mb-2 text-base font-bold text-center capitalize border-b border-gray-200'
			title.textContent = legend.title
			section.appendChild(title)

			// Determine number of columns based on item count
			const itemCount = legend.items.length
			let columns = 3 // Default to 3 columns for 3+ items
			if (itemCount === 1) {
				columns = 1
			} else if (itemCount === 2) {
				columns = 2
			}

			// Create a grid container for items
			const content = document.createElement('div')
			content.style.display = 'grid'
			content.style.gridTemplateColumns = `repeat(${columns}, 1fr)` // Equal-width columns
			content.style.gap = '16px' // Increased gap between items
			content.style.justifyItems = 'center' // Center items within each grid cell
			content.style.width = '100%'

			legend.items.forEach(item => {
				// Each item is a grid cell
				const itemContainer = document.createElement('div')
				itemContainer.style.display = 'flex'
				itemContainer.style.alignItems = 'center'
				itemContainer.style.gap = '8px' // Increased gap between icon and text
				itemContainer.style.width = '100%' // Make item container take full width of grid cell
				itemContainer.style.overflow = 'hidden' // Prevent overflow from container

				if (item.type === 'marker') {
					const marker = document.createElement('div')
					marker.className = 'relative min-w-[1rem] w-4 h-4 border rounded-full border-mx-gray-500 flex-shrink-0'
					marker.style.backgroundColor = item.color
					if (item.iconUrl) {
						const img = document.createElement('img')
						img.src = item.iconUrl
						img.alt = 'Legend Item'
						img.className = 'w-2.5 h-2.5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
						marker.appendChild(img)
					}
					itemContainer.appendChild(marker)
				} else if (item.type === 'line') {
					const line = document.createElement('div')
					line.className = `min-w-[2rem] w-8 border-2 ${item.style === 'dashed' ? 'border-dashed' : 'border-solid'} rounded flex-shrink-0`
					line.style.borderColor = item.color
					itemContainer.appendChild(line)
				}

				const label = document.createElement('span')
				label.className = 'text-xs font-medium cursor-pointer'
				label.style.overflow = 'hidden'
				label.style.textOverflow = 'ellipsis'
				label.style.whiteSpace = 'nowrap'
				label.style.flexGrow = '1' // Allow label to take remaining space
				label.style.flexShrink = '1' // Allow label to shrink if needed
				label.style.minWidth = '0' // Enable text truncation
				label.setAttribute('role', 'button')
				label.setAttribute('aria-label', 'View full label text')

				const itemName = item.name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
				label.textContent = itemName
				label.title = itemName // Full text on hover

				// Check if the label would be truncated
				const checkTruncation = () => {
					// Add visual indicator if text is truncated
					if (label.scrollWidth > label.clientWidth) {
						label.style.borderBottom = '1px dotted #888'
					} else {
						label.style.borderBottom = 'none'
					}
				}

				// Add event listeners for touch and click
				label.addEventListener('click', e => {
					e.stopPropagation()

					// Toggle between truncated and full text
					if (label.style.whiteSpace === 'nowrap') {
						label.style.whiteSpace = 'normal'
						label.style.position = 'relative'
						label.style.zIndex = '200'
						label.style.backgroundColor = 'transparent'
						label.style.padding = '2px 4px'
					} else {
						label.style.whiteSpace = 'nowrap'
						label.style.position = ''
						label.style.zIndex = ''
						label.style.backgroundColor = ''
						label.style.boxShadow = ''
						label.style.padding = ''
						label.style.borderRadius = ''
					}
					checkTruncation()
				})

				// Run initial check when content is loaded
				setTimeout(checkTruncation, 0)

				// Update truncation status when container is shown
				this.contentContainer.addEventListener('transitionend', () => {
					if (this.isExpanded) {
						checkTruncation()
					}
				})

				itemContainer.appendChild(label)
				content.appendChild(itemContainer)
			})

			section.appendChild(content)
			this.contentInnerContainer.appendChild(section)
		})

		this.contentContainer.appendChild(this.contentInnerContainer)
		this.container.appendChild(this.contentContainer)
	}

	onAdd() {
		return this.container
	}

	toggleVisibility() {
		this.isExpanded = !this.isExpanded

		if (this.isExpanded) {
			this.contentContainer.style.maxHeight = `${this.contentContainer.scrollHeight}px`
			this.contentContainer.style.opacity = '1'
			this.contentContainer.style.transform = 'scaleY(1)'
			this.contentContainer.style.zIndex = '100' // Ensure high z-index when expanded
		} else {
			this.contentContainer.style.maxHeight = '0'
			this.contentContainer.style.opacity = '0'
			this.contentContainer.style.transform = 'scaleY(0)'
		}
	}

	onRemove() {
		this.container.parentNode?.removeChild(this.container)
	}
}

class FilterConflictControl implements IControl {
	container: HTMLDivElement
	badge: HTMLSpanElement
	message: HTMLParagraphElement
	detailsContainer: HTMLDivElement
	list: HTMLUListElement
	headerButton: HTMLButtonElement
	conflicts: FilterConflict[]
	isExpanded: boolean
	map?: Map

	constructor(conflicts: FilterConflict[] = []) {
		this.conflicts = conflicts
		this.isExpanded = false
		this.container = document.createElement('div')
		this.container.className = 'pointer-events-auto mapboxgl-ctrl mx-filter-conflict-control'
		this.container.style.maxWidth = '360px'
		this.container.style.display = 'none'

		const wrapper = document.createElement('div')
		wrapper.className = 'flex flex-col gap-2 border rounded-md shadow-lg border-mx-error/40 bg-white/95 shadow-red-500/10 text-mx-gray-800 dark:border-mx-error-dark/60 dark:bg-mx-green-900/90 dark:text-mx-gray-300'
		wrapper.style.backdropFilter = 'blur(6px)'
		wrapper.style.padding = '12px'

		const headerButton = document.createElement('button')
		headerButton.type = 'button'
		headerButton.className = 'flex items-center w-full gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-mx-error/60 dark:focus-visible:ring-mx-error-dark/60'
		headerButton.onclick = () => {
			this.isExpanded = !this.isExpanded
			this.render()
		}
		headerButton.setAttribute('aria-expanded', 'false')
		headerButton.classList.add('cursor-pointer')
		this.headerButton = headerButton

		const statusDot = document.createElement('span')
		statusDot.className = 'relative flex h-2.5 w-2.5'
		statusDot.innerHTML = `
			<span class="absolute inline-flex h-full w-full animate-ping dark:animate-ping rounded-full bg-mx-error/80 dark:bg-mx-error-dark opacity-75 dark:opacity-100"></span>
			<span class="relative inline-flex h-2.5 w-2.5 rounded-full bg-mx-error dark:bg-mx-error-dark"></span>
		`

		const textContainer = document.createElement('div')
		textContainer.className = 'flex flex-col gap-0.5'

		const title = document.createElement('p')
		title.className = 'text-xs font-semibold tracking-wide uppercase text-mx-error dark:text-mx-error-dark'
		title.textContent = 'Filter conflicts detected'

		this.message = document.createElement('p')
		this.message.className = 'text-sm font-medium'
		this.message.textContent = ''

		textContainer.appendChild(title)

		this.badge = document.createElement('span')
		this.badge.className = 'inline-flex items-center justify-center px-3 py-1 ml-auto text-xs font-semibold rounded-full bg-mx-error/15 text-mx-error dark:bg-mx-error-dark/15 dark:text-mx-error-dark'
		this.badge.textContent = '0'

		headerButton.appendChild(statusDot)
		headerButton.appendChild(textContainer)
		headerButton.appendChild(this.badge)

		this.list = document.createElement('ul')
		this.list.className = 'flex flex-col gap-2 text-xs text-mx-gray-600 dark:text-mx-gray-200'
		this.list.style.display = 'none'

		this.detailsContainer = document.createElement('div')
		this.detailsContainer.className = 'flex flex-col gap-3 pt-2'
		this.detailsContainer.style.display = 'none'

		this.message = document.createElement('p')
		this.message.className = 'text-sm font-medium'
		this.message.textContent = ''
		this.detailsContainer.appendChild(this.message)
		this.detailsContainer.appendChild(this.list)

		wrapper.appendChild(headerButton)
		wrapper.appendChild(this.detailsContainer)

		this.container.appendChild(wrapper)
	}

	onAdd(map: Map) {
		this.map = map
		this.render()
		return this.container
	}

	onRemove() {
		this.container.remove()
		this.map = undefined
	}

	updateConflicts(conflicts: FilterConflict[]) {
		this.conflicts = conflicts
		this.render()
	}

	getContainer() {
		return this.container
	}

	private render() {
		const conflictCount = this.conflicts?.length ?? 0
		if (!conflictCount) {
			this.container.style.display = 'none'
			this.isExpanded = false
			this.detailsContainer.style.display = 'none'
			this.list.style.display = 'none'
			this.headerButton.setAttribute('aria-expanded', 'false')
			return
		}

		this.container.style.display = 'block'
		this.badge.textContent = conflictCount.toString()
		this.message.textContent = `${conflictCount} filter ${conflictCount === 1 ? 'rule needs' : 'rules need'} attention`
		this.message.style.display = this.isExpanded ? 'block' : 'none'
		this.detailsContainer.style.display = this.isExpanded ? 'flex' : 'none'
		this.headerButton.setAttribute('aria-expanded', this.isExpanded ? 'true' : 'false')
		this.list.innerHTML = ''
		for (const conflict of this.conflicts) {
			const item = document.createElement('li')
			item.className = 'px-3 py-2 border rounded-md shadow-sm border-mx-error/20 bg-mx-error/15 dark:border-mx-error-dark/20 dark:bg-mx-error-dark/15'
			item.innerHTML = `
				<div class="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-black/50 dark:text-mx-gray-300">
					<span>${conflict.direction === 'table-to-map' ? 'Table → Map' : 'Map → Table'}</span>
					<span>#${conflict.filterId}</span>
				</div>
				<p class="mt-1 text-sm font-semibold text-black/80 dark:text-mx-gray-200">
					${conflict.columnKey}
				</p>
				<p class="text-xs leading-5 text-mx-gray-500 dark:text-mx-gray-400">
					${conflict.reason}
				</p>
			`
			this.list.appendChild(item)
		}

		this.list.style.display = this.isExpanded ? 'flex' : 'none'
	}
}

class LassoActionControl implements IControl {
	// define elements, data, and functions
	container: HTMLDivElement
	labelContainer: HTMLDivElement
	lassoPolygonIds: string[]
	clearLassoPolygon: () => void
	getExportData: () => void
	showExportButton: boolean

	// initialize the control with styles, data, and functions
	constructor(
		lassoPolygonIds: string[],
		clearLassoPolygon: () => void,
		getExportData: () => void,
		showExportButton: boolean
	) {
		this.lassoPolygonIds = lassoPolygonIds
		// Bind the clearLassoPolygon function to preserve context
		this.clearLassoPolygon = clearLassoPolygon
		this.getExportData = getExportData
		this.showExportButton = showExportButton

		this.container = document.createElement('div')
		this.container.className = 'pointer-events-auto mapboxgl-ctrl'
		this.labelContainer = document.createElement('div')
		this.labelContainer.className = 'flex items-center justify-between p-2 bg-white rounded shadow-md'

		// Initialize visibility based on the initial label
		this.setVisibility()
	}

	onAdd() {
		// Create a text element to display the selected IDs
		// Only count unique IDs
		const uniqueIds = Array.from(new Set(this.lassoPolygonIds))
		const text = document.createElement('p')
		text.textContent = `${uniqueIds.length} selected`
		text.className = 'pr-2 text-sm font-bold capitalize whitespace-nowrap'
		this.labelContainer.appendChild(text)

		if (this.showExportButton) {
			// Create the "Export" button to copy the GeoJSON to the clipboard
			const exportButton = document.createElement('button')
			exportButton.textContent = 'Export'
			exportButton.className = 'px-2 py-1 ml-2 text-white rounded shadow-sm'
			exportButton.style.backgroundColor = '#000000'
			exportButton.style.transition = 'background-color 0.1s ease'
			exportButton.onmouseenter = () => {
				exportButton.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
			}
			exportButton.onmouseleave = () => {
				exportButton.style.backgroundColor = '#000000'
			}

			// Add the click handler to call getExportData
			exportButton.onclick = () => {
				this.getExportData()
			}

			this.labelContainer.appendChild(exportButton)
		}

		// Append the label container to the control container
		this.container.appendChild(this.labelContainer)

		// Return the control container
		return this.container
	}

	updateLabel(lassoPolygonIds: string[]) {
		this.lassoPolygonIds = lassoPolygonIds
		const uniqueIds = Array.from(new Set(lassoPolygonIds))
		const text = this.labelContainer.children[0] as HTMLParagraphElement
		text.textContent = `${uniqueIds.length} selected`
		this.setVisibility()
	}

	setVisibility() {
		// Hide the container if there are no selected IDs
		this.container.style.display = this.lassoPolygonIds.length ? 'flex' : 'none'
	}

	onRemove() {
		this.container.parentNode?.removeChild(this.container)
	}
}

export {
    ActivePolygonControl,
    FilterConflictControl,
    LassoActionControl,
    LayerVisibilityControl,
    LegendControl,
    LoadingOverlayControl,
    StyleSwitcherControl
}

